import {
  Component,
  DestroyRef,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import {
  NOTIFICATION_STATE_NAME,
  SHOPCHAT_JS_URL,
  TIME_TO_RESHOW_NOTIFICATION_MODAL,
} from '../../app.constants';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { DomSanitizer } from '@angular/platform-browser';
import { CommonModule, isPlatformBrowser, NgIf } from '@angular/common';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Bind } from 'primeng/bind';
import { TieredMenu } from 'primeng/tieredmenu';
import { Toast } from 'primeng/toast';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { FormsModule } from '@angular/forms';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Button } from 'primeng/button';
import { ApiService } from '../../_utils/api/api.service';
import { PushNotificationService } from '../../_utils/push-notification/push-notification.service';
import { PwaService } from '../../_utils/pwa/pwa.service';
import { StorageService } from '../../_utils/storage/storage.service';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.scss',
    providers: [MessageService],
    imports: [CommonModule, TranslocoDirective, Bind, TieredMenu, Toast, Dialog, Message, FormsModule, ToggleSwitch, Button]
})
export class NavbarComponent implements OnInit {
  showNavbar = signal(false);
  showBell = signal(true);
  bannerPWAModal = signal(false);
  data: any = {};
  config: any = {};
  notificationModal = signal({ visible: false, loading: false, error: null });
  notificationModel: any = { privacy_consent: false, webpush: true };
  telegram: string | null = null;
  offline = signal(false);
  currentLang: string;
  userLang: string = 'en';
  languages: MenuItem[] = [];

  private readonly langsMap: { [key: string]: string } = {
    it: 'Italiano',
    en: 'English',
    es: 'Español',
    fr: 'Français',
    de: 'Deutsch',
    sq: 'Shqip'
  };

  constructor(
    private translocoService: TranslocoService,
    private destroyRef: DestroyRef,
    private messageService: MessageService,
    protected router: Router,
    protected storageService: StorageService,
    private pwaService: PwaService,
    private apiService: ApiService,
    protected pushNotificationService: PushNotificationService,
    protected sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.currentLang = this.translocoService.getActiveLang();
    if (isPlatformBrowser(this.platformId))
      this.userLang = navigator ? navigator.language : '';
  }

  @HostListener('keydown.enter', ['$event']) onEnterKeyDown(
    e: Event
  ): void {
    const keyboardEvent = e as KeyboardEvent;
    keyboardEvent?.preventDefault();

    if (keyboardEvent?.target) {
      let targetEvent = keyboardEvent.target as HTMLSpanElement;
      if (targetEvent.classList.contains('notification-modal-event')) {
        this.notificationModal.set({ ...this.notificationModal(), visible: true });
      }
    }
  }
  
  showNotificationOrBannerModal(notificationActive: boolean): void {
    setTimeout(() => {
      while (true) {

        console.log(this.data);

        if (this.data) {
          if (notificationActive) {
            this.showBell.set(true);
          } else {
            if (this.showBell()) {
              this.notificationModal.set({ ...this.notificationModal(), visible: true });
            } else {
              if (this.config.pwa_banner_link) this.bannerPWAModal.set(true);
            }
          }

          break;
        }
      }
    }, 3000);
  }

  goToLink(url: string) {
    window.open(url, '_blank');
  }

  ngOnInit(): void {
    if (typeof window === 'undefined') return;

    this.translocoService
      .selectTranslate(['warning', 'pwa_offline'])
      .subscribe((translations) => {
        // check for network status
        this.pwaService.networkStatus().subscribe({
          next: (status) => {
            this.offline.set(!status);
            if (this.offline()) {
              console.warn('Not connected, PWA offline');
              this.messageService.add({
                severity: 'warn',
                sticky: true,
                summary: translations[0],
                detail: translations[1],
              });
            } else {
              this.messageService.clear();
            }
          },
          error: (err) => console.error(err),
        });
      });

    // set the notification values for the notification model
    let notification = this.storageService.getValue(NOTIFICATION_STATE_NAME);
    if (notification && notification.status == 'subscribed' && (notification?.email || notification?.webpush)) {
      this.notificationModel = {
        email: notification?.email || null,
        emailEnabled: notification?.email ? true : false,
        webpush: notification?.webpush || false,
      };

      this.showNotificationOrBannerModal(
        notification?.email || notification?.webpush
      );
    } else {
      // show notification modal if web push is supported, for a max of 3 attempts to remind the user
      // to subscribe to the notification
      if (
        this.pushNotificationService.isSupported() &&
        this.pwaService.isInstalled()
      ) {
        let attempts = notification?.attempts || 0,
          last_attempt = notification?.last_attempt || 0;
        if (
          attempts < 3 &&
          Date.now() - last_attempt >= TIME_TO_RESHOW_NOTIFICATION_MODAL
        ) {
          this.showNotificationOrBannerModal(false);

          this.storageService.updateValue(NOTIFICATION_STATE_NAME, {
            status: 'not-subscribed',
            attempts: attempts + 1,
            last_attempt: Date.now(),
            token: null,
            email: null,
            webpush: false,
          });
        }
      }
    }

    // get company information
    this.apiService.getCompany().subscribe({
      next: (data) => {
        this.data = data.data;
        this.config = data.config;

        //TODO test
        //if (this.config.pwa_banner_link) this.bannerPWAModal = true;

        this.telegram = this.data.canale_telegram
          ? JSON.parse(this.data.canale_telegram)['chatID']
          : null;
        this.showNavbar.set(true);

        if (!this.storageService.getCookie('lang')) {
          const langs = ['it', ...data.data.languages];
          const lang = this.userLang.split('-')[0];
          if (langs.includes(lang)) this.translocoService.setActiveLang(lang);
          else this.translocoService.setActiveLang('en');
        }

        let generateMenuItemHtml = (lang: string, label: string) =>
          `<span class="flex"><img src="/flags/${lang}.svg" width="22" alt="${lang} flag" draggable="false"><span class="ml-2">${label}</span></span>`;

        this.currentLang = this.translocoService.getActiveLang();
        this.languages = [
          {
            label: generateMenuItemHtml('it', 'Italiano'),
            escape: false,
            command: (event) => this.changeLang('it'),
          }
        ];

        const langs = this.data.languages.filter((lang: string) => lang != 'it');
        langs.forEach((lang: string) => {
          if (!this.langsMap[lang]) return;

          this.languages.push({
            label: generateMenuItemHtml(lang, this.langsMap[lang]),
            escape: false,
            command: (event) => this.changeLang(lang),
          });
        });

        if (this.data.throwLimitUser) {
          this.showBell.set(false);
        }

        if (this.data.shopchat_uuid) {
          this.loadShopChatScript(this.data.shopchat_uuid)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                const shopchat = (window as any).shopchat;
                console.log('ShopChat loaded');
                console.log('Setting ShopChat language to', this.currentLang);
                shopchat.setActiveLang(this.currentLang);
                this.translocoService.langChanges$
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe((lang) => {
                    console.log('Changing ShopChat language to', lang);
                    shopchat.setActiveLang(lang);
                  });
                console.log('ShopChat ready', (window as any).shopchat);},
              error: (err) => console.error('Error loading ShopChat', err),
            });
        }
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  /**
   * Change current language
   * @param lang string of the language code
   */
  changeLang(lang: string) {
    const langs = ['it', ...this.data.languages];
    if (langs.includes(lang)) {
      this.currentLang = lang;
      this.translocoService.setActiveLang(lang);

      this.pwaService.getUserUID(true).subscribe({
        next: (uid) => {
          this.apiService.updateLang(uid, lang).subscribe({
            next: (res) => console.log(res),
            error: (err) => console.error(err),
          });
        },
        error: (err) => console.error(err),
      });
    } else console.error('Language not supported');
  }

  /**
   * Called when the user click the botton of the modal to update notification state
   * @param event click event
   */
  notificationClick(event: any): void {
    this.notificationModal.set({ ...this.notificationModal(), loading: true, error: null });
    this.pushNotificationService
      .subscribe(
        this.notificationModel.webpush || false,
        /*this.notificationModel.email ||*/ null
      )
      .subscribe({
        next: (token) => {
          this.notificationModal.set({ visible: false, loading: false, error: null });
          this.storageService.updateValue(NOTIFICATION_STATE_NAME, {
            email: this.notificationModel.email || null,
            webpush: this.notificationModel.webpush || false,
            status: 'subscribed',
            token: token,
          });
        },
        error: (err) => {
          console.error(err);
          this.notificationModal.set({ ...this.notificationModal(), loading: false, error: err });
          this.storageService.updateValue(NOTIFICATION_STATE_NAME, {
            status: 'error',
            token: null,
            webpush: false,
            email: null,
          });
        },
      });
  }

  loadShopChatScript(shopchatUuid: string): Observable<boolean> {
    const globalObj = () => (window as any).shopchat;

    return new Observable<boolean>((observer) => {
      // If already available, resolve immediately
      if (globalObj()) {
        observer.next(true);
        observer.complete();
        return;
      }

      // Avoid loading the script multiple times
      const existingScript = Array.from(
        document.querySelectorAll('script')
      ).find((s) => s.src.includes(SHOPCHAT_JS_URL));

      const node = existingScript || document.createElement('script');

      if (!existingScript) {
        node.src = SHOPCHAT_JS_URL;
        node.setAttribute('public_key', shopchatUuid);
        node.setAttribute('marginBottom', '80px');
        node.setAttribute('marginRight', '20px');
        node.type = 'text/javascript';
        node.defer = true;
        document.getElementsByTagName('head')[0].appendChild(node);
      }

      let interval: any;
      let timeoutHandle: any;

      const clear = () => {
        if (interval) clearInterval(interval);
        if (timeoutHandle) clearTimeout(timeoutHandle);
      };

      const onReady = () => {
        clear();
        observer.next(true);
        observer.complete();
      };

      const onError = (err: any) => {
        clear();
        observer.error(err);
      };

      node.addEventListener('error', onError, { once: true });

      const startPolling = () => {
        interval = setInterval(() => {
          if (globalObj()) {
            onReady();
          }
        }, 100);

        // fail after 10s if not ready
        timeoutHandle = setTimeout(() => {
          onError(new Error('ShopChat script loaded but global object not available'));
        }, 10000);
      };

      if (existingScript) {
        // If script was already loaded, just poll
        startPolling();
      } else {
        node.addEventListener(
          'load',
          () => {
            startPolling();
          },
          { once: true }
        );
      }

      return () => clear();
    });
  }
}
